import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Avatar from '@mui/material/Avatar';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ApprovalExpandablePanel from 'v2/apps/shared/components/approval-expandable-panel';
import MuiDropdownButton from 'v2/apps/clink/pages/shared/MuiDropdown';
import mapAssignedApproversToLevels from 'v2/helpers/approvers';
import parseCurrency, { currencyConfig } from 'v2/helpers/currency';
import { getAccountLogo } from 'v2/helpers/user';
import { resolveLoiStatus } from 'v2/helpers/status/loi';
import {
  AvtarGridContainer,
  CompanyGridContainer,
  StatusGridContainer,
} from 'v2/apps/clink/pages/orders/Mui.Components';
import LoiStatusCell from './LoiStatusCell';
import getLoiActions from './useLoiActions';

// The API sends `Y-m-d H:i:s`; parse it explicitly rather than relying on
// Date's implementation-defined handling of the space-separated form.
const formatDate = (date) => {
  if (!date) return 'N/A';
  const parsed = moment(date, 'YYYY-MM-DD HH:mm:ss');
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : 'N/A';
};

const acronymOf = (name) =>
  (name ?? '')
    .split(' ')
    .filter(Boolean)
    .reduce((acc, word) => acc + word[0], '');

/**
 * A Letter of Intent row in the Orders table.
 *
 * Deliberately mirrors the order row's cell structure so the two sit in one
 * table without the columns drifting, and reuses `ApprovalExpandablePanel`
 * rather than introducing a second approval UI.
 */
const LoiRow = ({
  row,
  expanded,
  onToggleExpand,
  onRejectionClick,
  onCreateFinalOrder,
  onWithdrawApproval,
  onViewLogs,
  sendApprovalReminder,
  canSendReminder,
  showDocumentColumn,
  columnCount,
  rowKey,
}) => {
  const { t } = useTranslation();

  // Expiry is time-based only and never overrides a terminal status.
  const status = resolveLoiStatus(row.status, row.expiry_date);
  const levels = mapAssignedApproversToLevels(
    row.assigned_approvers,
    row.isLevel,
  );
  const { subcontractor = {}, document = {} } = row;

  const actions = getLoiActions(row, status, {
    rowKey,
    onToggleExpand,
    onRejectionClick,
    onWithdrawApproval,
    onCreateFinalOrder,
    onViewLogs,
    hasApprovers: levels.length > 0,
  });

  return (
    <>
      <TableRow data-testid={`loi-row-${rowKey}`}>
        <TableCell>
          <Grid item>
            {levels.length > 0 && (
              <IconButton
                data-testid={`loi-expand-btn-${rowKey}`}
                aria-label="expand row"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleExpand(rowKey);
                }}
              >
                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
            {t('letter-of-intent')}
          </Grid>
        </TableCell>

        <TableCell>{row.loi_number}</TableCell>

        {showDocumentColumn && (
          <TableCell>
            <Tooltip title={document.name ?? ''}>
              <Button>{acronymOf(document.name)}</Button>
            </Tooltip>
          </TableCell>
        )}

        <TableCell>{formatDate(row.created_at)}</TableCell>

        <TableCell>
          <Grid container spacing={2}>
            <AvtarGridContainer>
              <Avatar src={getAccountLogo(subcontractor.id, true)} />
            </AvtarGridContainer>
            <CompanyGridContainer>{subcontractor.name}</CompanyGridContainer>
          </Grid>
        </TableCell>

        <TableCell>
          {parseCurrency(row.value, currencyConfig[t('currency')])}
        </TableCell>

        <TableCell>
          <StatusGridContainer>
            <Grid item>
              <LoiStatusCell
                row={row}
                status={status}
                rawStatusLabel={row.rawStatusLabel}
                onRejectionClick={onRejectionClick}
              />
            </Grid>
            <Grid item>
              {actions.length > 0 && (
                <MuiDropdownButton
                  data-testid={`loi-actions-${rowKey}`}
                  options={actions}
                />
              )}
            </Grid>
          </StatusGridContainer>
        </TableCell>
      </TableRow>

      <TableRow data-testid={`loi-approval-row-${rowKey}`}>
        <TableCell colSpan={columnCount}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <ApprovalExpandablePanel
              levels={levels}
              entity_id={row.loi_id}
              sendApprovalReminder={sendApprovalReminder}
              canSendReminder={canSendReminder}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

LoiRow.propTypes = {
  row: PropTypes.shape({
    loi_id: PropTypes.number,
    loi_number: PropTypes.string,
    status: PropTypes.string,
    rawStatusLabel: PropTypes.string,
    expiry_date: PropTypes.string,
    created_at: PropTypes.string,
    value: PropTypes.number,
    subcontractor: PropTypes.object,
    submitted_by: PropTypes.object,
    document: PropTypes.object,
    assigned_approvers: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
    ]),
    isLevel: PropTypes.bool,
    is_signed: PropTypes.bool,
    tender_id: PropTypes.number,
    signatory: PropTypes.shape({
      total: PropTypes.number,
      signers: PropTypes.number,
    }),
  }).isRequired,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func.isRequired,
  onRejectionClick: PropTypes.func,
  onCreateFinalOrder: PropTypes.func,
  onWithdrawApproval: PropTypes.func,
  onViewLogs: PropTypes.func,
  sendApprovalReminder: PropTypes.func,
  canSendReminder: PropTypes.bool,
  showDocumentColumn: PropTypes.bool,
  columnCount: PropTypes.number.isRequired,
  rowKey: PropTypes.string.isRequired,
};

export default LoiRow;
